/**
 * Catching a render that throws.
 *
 * WHY THIS IS NOT OPTIONAL
 *   React unmounts the entire tree when a render throws and nothing catches
 *   it. Without a boundary, one bad comment body, one malformed change row,
 *   one unexpected undefined anywhere in the product takes the whole window to
 *   a blank page — and a blank page tells the reader nothing, loses their
 *   place, and looks like the application is gone rather than one part of it.
 *
 *   The replica is safe throughout: it lives in the store and in IndexedDB,
 *   not in the component that failed. So the honest thing to offer is "this
 *   part broke, here is what it said, try again" rather than a reload that
 *   would throw away nothing but also fix nothing.
 *
 * DATA DEFINITION
 *   A BoundaryState is { error?: Error }.
 *   Interpretation: the error that took this subtree down, or nothing while it
 *   is healthy. `resetKey` changing means the caller believes the cause has
 *   gone — typically because the person navigated somewhere else — so we try
 *   rendering again.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Button } from './ui/primitives'

interface Props {
  children: ReactNode
  /** What broke, in words the reader can act on. */
  what?: string
  /** Changing this clears the error and re-renders the children. */
  resetKey?: string | number
}

interface State {
  error?: Error
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = {}

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // There is no error-reporting service here by design; the console is
    // where a developer will look, and the component stack is the part that
    // actually locates the fault.
    console.error(`Lattice: ${this.props.what ?? 'a component'} failed to render`, error, info.componentStack)
  }

  override componentDidUpdate(previous: Props): void {
    if (this.state.error && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: undefined })
    }
  }

  override render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <div className="flex h-full min-h-40 items-center justify-center p-6">
        <div className="max-w-sm text-center">
          <AlertTriangle className="mx-auto h-6 w-6 text-[var(--modified)]" strokeWidth={1.5} />
          <p className="mt-2 text-sm font-medium">
            {this.props.what ? `${this.props.what} could not be shown` : 'Something could not be shown'}
          </p>
          <p className="mt-1 text-sm leading-relaxed text-[var(--fg-subtle)]">
            The rest of Lattice is unaffected and nothing has been lost — your files and
            history are stored, not held in this view.
          </p>
          <pre className="mt-2 max-h-24 overflow-auto rounded-[var(--radius-sm)] bg-[var(--bg-inset)] p-2 text-left font-mono text-[11px] text-[var(--fg-muted)]">
            {error.message}
          </pre>
          <div className="mt-3 flex justify-center gap-2">
            <Button variant="secondary" onClick={() => this.setState({ error: undefined })}>
              Try again
            </Button>
          </div>
        </div>
      </div>
    )
  }
}
